import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Typography, Button, Grid, Card, CardContent,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip,
    Paper, Avatar, Fade, IconButton, Stepper, Step, StepLabel,
    CircularProgress, Switch, FormControlLabel, MenuItem, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClassIcon from '@mui/icons-material/Class';
import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import LayersIcon from '@mui/icons-material/Layers';
import SchoolIcon from '@mui/icons-material/School';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { listModel, ModelKey } from '../config/aiConfig';
import { useToast } from '../context/ToastContext';

interface Parcours {
    _id: string;
    title: string;
    description: string;
    blocs: any[];
}

interface GeneratedBloc {
    title: string;
    description: string;
    order: number;
    competencies: string[];
    categories?: GeneratedCategory[];
}

interface GeneratedCategory {
    title: string;
    description: string;
    order: number;
    tags: string[];
}

const WIZARD_STEPS = ['Informations', 'Parcours & Blocs', 'Catégories', 'Finalisation'];

const ParcoursList = () => {
    const [parcoursList, setParcoursList] = useState<Parcours[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [newParcours, setNewParcours] = useState({ title: '', description: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const auth = useContext(AuthContext);
    const { showToast } = useToast();

    // AI Wizard States
    const [isAiMode, setIsAiMode] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);

    // Step 0: Input data
    const [aiInput, setAiInput] = useState({
        title: '',
        description: '',
        franceCompetence: '',
        modelChoose: 'default' as ModelKey
    });

    // Step 1: Generated structure
    const [generatedParcours, setGeneratedParcours] = useState<{ title: string; description: string } | null>(null);
    const [generatedBlocs, setGeneratedBlocs] = useState<GeneratedBloc[]>([]);

    // Step 2: Categories per bloc (index-based)
    const [currentBlocIndex, setCurrentBlocIndex] = useState(0);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    useEffect(() => {
        fetchParcours();
    }, []);

    const fetchParcours = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/course/parcours');
            if (Array.isArray(res.data)) {
                setParcoursList(res.data);
            } else {
                setParcoursList([]);
            }
        } catch (err) {
            console.error("Error fetching parcours", err);
            setParcoursList([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            await api.post('/api/course/parcours', newParcours);
            setOpen(false);
            setNewParcours({ title: '', description: '' });
            fetchParcours();
        } catch (err) {
            console.error("Error creating parcours", err);
        }
    };

    const resetWizard = () => {
        setActiveStep(0);
        setAiInput({ title: '', description: '', franceCompetence: '', modelChoose: 'default' });
        setGeneratedParcours(null);
        setGeneratedBlocs([]);
        setCurrentBlocIndex(0);
        setIsAiMode(false);
    };

    const handleClose = () => {
        setOpen(false);
        resetWizard();
        setNewParcours({ title: '', description: '' });
    };

    // Step 1: Generate structure
    const handleGenerateStructure = async () => {
        if (!aiInput.title.trim() || !aiInput.description.trim()) {
            showToast('Veuillez remplir le titre et la description', 'error');
            return;
        }

        setGenerating(true);
        try {
            const res = await api.post('/api/course/ai-parcours/generate-structure', aiInput);
            setGeneratedParcours(res.data.parcours);
            setGeneratedBlocs(res.data.blocs.map((b: GeneratedBloc) => ({ ...b, categories: [] })));
            setActiveStep(1);
            showToast('Structure générée avec succès!', 'success');
        } catch (err: any) {
            console.error("Error generating structure", err);
            if (err.response?.status === 429) {
                showToast("Quota OpenAI dépassé. Réessayez plus tard.", "error");
            } else {
                showToast("Erreur lors de la génération", "error");
            }
        } finally {
            setGenerating(false);
        }
    };

    // Step 2: Generate categories for current bloc
    const handleGenerateCategories = async () => {
        if (currentBlocIndex >= generatedBlocs.length) return;

        setCategoriesLoading(true);
        const allCategories: GeneratedBloc[] = [...generatedBlocs];

        try {
            // Generate categories for all blocs sequentially
            for (let i = 0; i < allCategories.length; i++) {
                if (!allCategories[i].categories || allCategories[i].categories!.length === 0) {
                    const res = await api.post('/api/course/ai-parcours/generate-categories', {
                        bloc: allCategories[i],
                        parcoursTitle: generatedParcours?.title,
                        modelChoose: aiInput.modelChoose
                    });
                    allCategories[i].categories = res.data.categories;
                }
            }

            setGeneratedBlocs(allCategories);
            setActiveStep(2);
            showToast('Catégories générées pour tous les blocs!', 'success');
        } catch (err: any) {
            console.error("Error generating categories", err);
            if (err.response?.status === 429) {
                showToast("Quota OpenAI dépassé. Réessayez plus tard.", "error");
            } else {
                showToast("Erreur lors de la génération des catégories", "error");
            }
        } finally {
            setCategoriesLoading(false);
        }
    };

    // Step 3: Save everything
    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await api.post('/api/course/ai-parcours/save', {
                parcours: generatedParcours,
                blocs: generatedBlocs
            });
            showToast('Parcours créé avec succès!', 'success');
            handleClose();
            fetchParcours();
            navigate(`/parcours/${res.data.parcoursId}`);
        } catch (err) {
            console.error("Error saving parcours", err);
            showToast("Erreur lors de la sauvegarde", "error");
        } finally {
            setSaving(false);
        }
    };

    // Edit handlers for generated content
    const updateBlocField = (index: number, field: keyof GeneratedBloc, value: any) => {
        const updated = [...generatedBlocs];
        (updated[index] as any)[field] = value;
        setGeneratedBlocs(updated);
    };

    const removeBloc = (index: number) => {
        setGeneratedBlocs(generatedBlocs.filter((_, i) => i !== index));
    };

    const addBloc = () => {
        setGeneratedBlocs([...generatedBlocs, {
            title: 'Nouveau bloc',
            description: '',
            order: generatedBlocs.length + 1,
            competencies: [],
            categories: []
        }]);
    };

    const updateCategoryField = (blocIndex: number, catIndex: number, field: keyof GeneratedCategory, value: any) => {
        const updated = [...generatedBlocs];
        if (updated[blocIndex].categories) {
            (updated[blocIndex].categories![catIndex] as any)[field] = value;
        }
        setGeneratedBlocs(updated);
    };

    const removeCategory = (blocIndex: number, catIndex: number) => {
        const updated = [...generatedBlocs];
        if (updated[blocIndex].categories) {
            updated[blocIndex].categories = updated[blocIndex].categories!.filter((_, i) => i !== catIndex);
        }
        setGeneratedBlocs(updated);
    };

    const addCategory = (blocIndex: number) => {
        const updated = [...generatedBlocs];
        if (!updated[blocIndex].categories) updated[blocIndex].categories = [];
        updated[blocIndex].categories!.push({
            title: 'Nouvelle catégorie',
            description: '',
            order: updated[blocIndex].categories!.length + 1,
            tags: []
        });
        setGeneratedBlocs(updated);
    };

    const filteredParcours = parcoursList.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalParcours = parcoursList.length;
    const totalBlocs = parcoursList.reduce((acc, p) => acc + (p.blocs?.length || 0), 0);

    // Render wizard step content
    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Titre du parcours"
                            fullWidth
                            required
                            value={aiInput.title}
                            onChange={(e) => setAiInput({ ...aiInput, title: e.target.value })}
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <TextField
                            margin="dense"
                            label="Description du parcours"
                            fullWidth
                            required
                            multiline
                            rows={3}
                            value={aiInput.description}
                            onChange={(e) => setAiInput({ ...aiInput, description: e.target.value })}
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <TextField
                            margin="dense"
                            label="Fiche France Compétences (optionnel)"
                            fullWidth
                            multiline
                            rows={4}
                            value={aiInput.franceCompetence}
                            onChange={(e) => setAiInput({ ...aiInput, franceCompetence: e.target.value })}
                            placeholder="Collez ici le contenu de la fiche France Compétences ou laissez vide..."
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        {/* Model selector */}
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(102, 126, 234, 0.05)', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
                            <TextField
                                select
                                label="Modèle IA"
                                fullWidth
                                value={aiInput.modelChoose}
                                onChange={(e) => setAiInput({ ...aiInput, modelChoose: e.target.value as ModelKey })}
                                size="small"
                            >
                                {(Object.keys(listModel) as ModelKey[]).map((key) => (
                                    <MenuItem key={key} value={key}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start', gap: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{listModel[key].name}</Typography>
                                                    <Chip label={listModel[key].model} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontWeight: 600 }} />
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">{listModel[key].description}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 500 }}>⏱ {listModel[key].time}</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                        {[...Array(10)].map((_, i) => (
                                                            <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', background: i < listModel[key].qualityScore ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(0,0,0,0.1)' }} />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea', whiteSpace: 'nowrap' }}>{listModel[key].price}€</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(102, 126, 234, 0.08)' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {listModel[aiInput.modelChoose].name} • ⏱ {listModel[aiInput.modelChoose].time} • Qualité: {listModel[aiInput.modelChoose].qualityScore}/10
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );

            case 1:
                return (
                    <Box sx={{ mt: 2 }}>
                        {/* Parcours info */}
                        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '2px solid #667eea' }}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 700 }}>📚 Parcours</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                label="Titre"
                                value={generatedParcours?.title || ''}
                                onChange={(e) => setGeneratedParcours({ ...generatedParcours!, title: e.target.value })}
                                sx={{ mb: 1 }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Description"
                                multiline
                                rows={2}
                                value={generatedParcours?.description || ''}
                                onChange={(e) => setGeneratedParcours({ ...generatedParcours!, description: e.target.value })}
                            />
                        </Paper>

                        {/* Blocs */}
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                            🧱 Blocs de compétences ({generatedBlocs.length})
                            <Button size="small" startIcon={<AddIcon />} onClick={addBloc}>Ajouter</Button>
                        </Typography>
                        {generatedBlocs.map((bloc, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'rgba(102, 126, 234, 0.03)' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary">Bloc {index + 1}</Typography>
                                    <IconButton size="small" color="error" onClick={() => removeBloc(index)}><DeleteIcon fontSize="small" /></IconButton>
                                </Box>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Titre"
                                    value={bloc.title}
                                    onChange={(e) => updateBlocField(index, 'title', e.target.value)}
                                    sx={{ mb: 1 }}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Description"
                                    value={bloc.description}
                                    onChange={(e) => updateBlocField(index, 'description', e.target.value)}
                                    sx={{ mb: 1 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Compétences: {bloc.competencies.join(', ') || 'Aucune'}
                                </Typography>
                            </Paper>
                        ))}
                    </Box>
                );

            case 2:
                return (
                    <Box sx={{ mt: 2, maxHeight: 400, overflow: 'auto' }}>
                        {generatedBlocs.map((bloc, blocIndex) => (
                            <Paper key={blocIndex} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#667eea' }}>
                                    🧱 {bloc.title}
                                </Typography>
                                <Box sx={{ pl: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">Catégories ({bloc.categories?.length || 0})</Typography>
                                        <Button size="small" onClick={() => addCategory(blocIndex)}>+ Ajouter</Button>
                                    </Box>
                                    {bloc.categories?.map((cat, catIndex) => (
                                        <Paper key={catIndex} elevation={0} sx={{ p: 1.5, mb: 1, bgcolor: 'rgba(102, 126, 234, 0.05)', borderRadius: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    value={cat.title}
                                                    onChange={(e) => updateCategoryField(blocIndex, catIndex, 'title', e.target.value)}
                                                    sx={{ flex: 1, mr: 1 }}
                                                />
                                                <IconButton size="small" color="error" onClick={() => removeCategory(blocIndex, catIndex)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Description"
                                                value={cat.description}
                                                onChange={(e) => updateCategoryField(blocIndex, catIndex, 'description', e.target.value)}
                                                sx={{ mt: 1 }}
                                            />
                                            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {cat.tags.map((tag, tagIndex) => (
                                                    <Chip key={tagIndex} label={tag} size="small" sx={{ fontSize: '0.7rem' }} />
                                                ))}
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                );

            case 3:
                return (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <AutoAwesomeIcon sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Prêt à créer!</Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            Votre parcours "{generatedParcours?.title}" avec {generatedBlocs.length} blocs et {generatedBlocs.reduce((acc, b) => acc + (b.categories?.length || 0), 0)} catégories va être créé.
                        </Typography>
                        <Paper sx={{ p: 2, textAlign: 'left', bgcolor: 'rgba(102, 126, 234, 0.05)', borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{generatedParcours?.title}</Typography>
                            <Typography variant="caption" color="text.secondary">{generatedParcours?.description}</Typography>
                            <Divider sx={{ my: 1 }} />
                            {generatedBlocs.map((bloc, i) => (
                                <Box key={i} sx={{ mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>• {bloc.title}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                                        {bloc.categories?.map(c => c.title).join(', ')}
                                    </Typography>
                                </Box>
                            ))}
                        </Paper>
                    </Box>
                );
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)', p: { xs: 2, md: 4 } }}>
            {/* Hero Header */}
            <Paper elevation={0} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 4, p: { xs: 3, md: 5 }, mb: 4, color: 'white', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ position: 'absolute', bottom: -30, right: 100, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56, backdropFilter: 'blur(10px)' }}>
                                <ClassIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>Gestion des Parcours</Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>Organisez vos programmes de formation par blocs et catégories</Typography>
                            </Box>
                        </Box>
                        {auth?.user?.role !== 'learner' && (
                            <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ mt: 2, bgcolor: 'white', color: '#667eea', fontWeight: 700, px: 4, py: 1.5, borderRadius: 3, textTransform: 'none', fontSize: '1rem', boxShadow: '0 4px 14px rgba(0,0,0,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.95)', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }, transition: 'all 0.2s ease' }}>
                                Créer un Parcours
                            </Button>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 3, p: 2.5, textAlign: 'center' }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{totalParcours}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>Parcours</Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 3, p: 2.5, textAlign: 'center' }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{totalBlocs}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>Blocs</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {/* Search Bar */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                <Paper elevation={0} sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: '100%', maxWidth: 600, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', transition: 'all 0.3s', '&:focus-within': { boxShadow: '0 8px 30px rgba(102, 126, 234, 0.15)', borderColor: '#667eea' } }}>
                    <IconButton sx={{ p: '10px', color: '#667eea' }}><SearchIcon /></IconButton>
                    <InputBase sx={{ ml: 1, flex: 1, fontFamily: 'Quicksand, sans-serif' }} placeholder="Rechercher un parcours..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </Paper>
            </Box>

            {/* Empty State */}
            {!loading && filteredParcours.length === 0 && (
                <Fade in timeout={500}>
                    <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, background: 'white', border: '2px dashed #e0e0e0' }}>
                        <SchoolIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#666', mb: 1 }}>Aucun parcours trouvé</Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>{searchTerm ? "Ajustez votre recherche" : "Commencez par créer votre premier parcours"}</Typography>
                        {!searchTerm && (
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 600, px: 4, py: 1.5, borderRadius: 3, textTransform: 'none' }}>
                                Créer mon premier parcours
                            </Button>
                        )}
                    </Paper>
                </Fade>
            )}

            {/* Parcours Grid */}
            <Grid container spacing={3}>
                {filteredParcours.map((parcours, index) => (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={parcours._id}>
                        <Fade in timeout={300 + index * 100}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4, overflow: 'hidden', position: 'relative', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', border: '1px solid rgba(102, 126, 234, 0.1)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)' } }}>
                                <Box sx={{ height: 10, background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }} />
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <Avatar sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', width: 48, height: 48 }}><LayersIcon /></Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif' }}>{parcours.title}</Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 48, lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {parcours.description || "Aucune description"}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Chip icon={<LayersIcon sx={{ fontSize: '1rem !important' }} />} label={`${parcours.blocs?.length || 0} Blocs`} size="small" sx={{ bgcolor: 'rgba(102, 126, 234, 0.08)', color: '#667eea', fontWeight: 600, borderRadius: 2 }} />
                                    </Box>
                                </CardContent>
                                <Box sx={{ p: 2, pt: 0, mt: 'auto' }}>
                                    <Button fullWidth variant="contained" endIcon={<EditIcon />} onClick={() => navigate(`/parcours/${parcours._id}`)} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 600, borderRadius: 2.5, textTransform: 'none', py: 1, boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)', '&:hover': { boxShadow: '0 6px 15px rgba(102, 126, 234, 0.3)' } }}>
                                        Modifier la structure
                                    </Button>
                                </Box>
                            </Card>
                        </Fade>
                    </Grid>
                ))}
            </Grid>

            {/* Create/AI Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, backdropFilter: 'blur(10px)', bgcolor: 'rgba(255,255,255,0.95)', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {isAiMode ? 'Créer un Parcours avec IA' : 'Créer un nouveau Parcours'}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f8f9fa', px: 2, py: 0.5, borderRadius: 2 }}>
                        <Typography variant="caption">{isAiMode ? 'Mode IA' : 'Manuel'}</Typography>
                        <Switch checked={isAiMode} onChange={(e) => { setIsAiMode(e.target.checked); setActiveStep(0); }} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#667eea' } }} />
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {isAiMode ? (
                        <>
                            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                                {WIZARD_STEPS.map((label) => (
                                    <Step key={label}><StepLabel>{label}</StepLabel></Step>
                                ))}
                            </Stepper>
                            {renderStepContent()}
                        </>
                    ) : (
                        <>
                            <TextField autoFocus margin="dense" label="Titre du parcours" fullWidth value={newParcours.title} onChange={(e) => setNewParcours({ ...newParcours, title: e.target.value })} sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                            <TextField margin="dense" label="Description" fullWidth multiline rows={4} value={newParcours.description} onChange={(e) => setNewParcours({ ...newParcours, description: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={handleClose} sx={{ color: 'text.secondary', fontWeight: 600 }}>Annuler</Button>

                    {isAiMode ? (
                        <>
                            {activeStep > 0 && (
                                <Button onClick={() => setActiveStep(activeStep - 1)} disabled={generating || categoriesLoading || saving}>
                                    Retour
                                </Button>
                            )}
                            {activeStep === 0 && (
                                <Button onClick={handleGenerateStructure} variant="contained" disabled={generating} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700, px: 4, borderRadius: 3, textTransform: 'none' }}>
                                    {generating ? <CircularProgress size={24} color="inherit" /> : 'Générer la structure'}
                                </Button>
                            )}
                            {activeStep === 1 && (
                                <Button onClick={handleGenerateCategories} variant="contained" disabled={categoriesLoading} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700, px: 4, borderRadius: 3, textTransform: 'none' }}>
                                    {categoriesLoading ? <CircularProgress size={24} color="inherit" /> : 'Générer les catégories'}
                                </Button>
                            )}
                            {activeStep === 2 && (
                                <Button onClick={() => setActiveStep(3)} variant="contained" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700, px: 4, borderRadius: 3, textTransform: 'none' }}>
                                    Valider et finaliser
                                </Button>
                            )}
                            {activeStep === 3 && (
                                <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700, px: 4, borderRadius: 3, textTransform: 'none' }}>
                                    {saving ? <CircularProgress size={24} color="inherit" /> : 'Créer le Parcours'}
                                </Button>
                            )}
                        </>
                    ) : (
                        <Button onClick={handleCreate} variant="contained" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700, px: 4, borderRadius: 3, textTransform: 'none' }}>
                            Créer le Parcours
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ParcoursList;
